import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, PenLine, ChevronRight, Check, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function ReflectionJournal({ api, paperId, onClose }) {
  const [prompts, setPrompts] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [responses, setResponses] = useState({});
  const [saved, setSaved] = useState({});
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [promptsRes, existingRes] = await Promise.all([
          axios.get(`${api}/reflection-prompts`, { withCredentials: true }),
          axios.get(`${api}/reflections/${paperId}`, { withCredentials: true }),
        ]);
        setPrompts(promptsRes.data);
        setExisting(existingRes.data);
        const pre = {};
        const savedKeys = {};
        existingRes.data.forEach((r) => {
          pre[r.prompt_key] = r.response_text;
          savedKeys[r.prompt_key] = true;
        });
        setResponses(pre);
        setSaved(savedKeys);
      } catch {
        toast.error("Failed to load reflection prompts");
      }
    };
    load();
  }, [api, paperId]);

  const saveReflection = async () => {
    const prompt = prompts[currentIdx];
    if (!prompt) return;
    const text = responses[prompt.key]?.trim();
    if (!text) {
      toast.error("Write a response before saving");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${api}/reflections`, {
        paper_id: paperId,
        prompt_key: prompt.key,
        response_text: text,
      }, { withCredentials: true });
      setSaved((prev) => ({ ...prev, [prompt.key]: true }));
      toast.success(`+${res.data.xp_earned} XP for reflecting!`);
    } catch {
      toast.error("Failed to save reflection");
    } finally {
      setSaving(false);
    }
  };

  const goNext = () => {
    if (currentIdx < prompts.length - 1) setCurrentIdx(currentIdx + 1);
  };
  const goPrev = () => {
    if (currentIdx > 0) setCurrentIdx(currentIdx - 1);
  };

  const totalAnswered = Object.keys(saved).length;
  const prompt = prompts[currentIdx];

  if (!prompts.length) return null;

  return (
    <div data-testid="reflection-journal">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-neo-purple border-2 border-neo-border dark:border-[#444] flex items-center justify-center">
            <PenLine className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-heading font-bold dark:text-white">Reflection Journal</h3>
            <p className="text-xs text-neo-text-secondary dark:text-gray-400">
              {totalAnswered}/{prompts.length} prompts completed (+25 XP each)
            </p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="neo-btn text-xs bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white" data-testid="reflection-close-btn">
            Close
          </button>
        )}
      </div>

      {/* Progress dots */}
      <div className="flex items-center gap-1.5 mb-4">
        {prompts.map((p, i) => (
          <button
            key={p.key}
            onClick={() => setCurrentIdx(i)}
            className={`w-3 h-3 rounded-full border-2 transition-all ${
              i === currentIdx
                ? "border-neo-purple bg-neo-purple scale-125"
                : saved[p.key]
                ? "border-neo-green bg-neo-green"
                : "border-gray-300 dark:border-gray-600 bg-transparent"
            }`}
            data-testid={`reflection-dot-${i}`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {prompt && (
          <motion.div
            key={prompt.key}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]">
              <p className="font-heading font-bold text-sm dark:text-white mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-neo-yellow flex-shrink-0" />
                {prompt.prompt}
              </p>
              <Textarea
                value={responses[prompt.key] || ""}
                onChange={(e) => setResponses((prev) => ({ ...prev, [prompt.key]: e.target.value }))}
                placeholder="Write your thoughts here..."
                className="border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg text-sm min-h-[100px]"
                rows={4}
                data-testid="reflection-textarea"
              />
              <div className="flex justify-between items-center mt-3 pt-3 border-t-2 border-neo-border/20 dark:border-[#444]/40">
                <button
                  onClick={goPrev}
                  disabled={currentIdx === 0}
                  className={`neo-btn text-xs ${currentIdx === 0 ? "bg-gray-200 text-gray-400 border-gray-300 shadow-none cursor-not-allowed" : "bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white"}`}
                  data-testid="reflection-prev"
                >
                  Previous
                </button>
                <div className="flex items-center gap-2">
                  {saved[prompt.key] && (
                    <span className="flex items-center gap-1 text-xs text-neo-green font-bold">
                      <Check className="w-3 h-3" /> Saved
                    </span>
                  )}
                  <button
                    onClick={saveReflection}
                    disabled={saving || !responses[prompt.key]?.trim()}
                    className={`neo-btn text-xs ${saving || !responses[prompt.key]?.trim() ? "bg-gray-200 text-gray-400 border-gray-300 shadow-none" : "neo-btn-purple"}`}
                    data-testid="reflection-save"
                  >
                    {saving ? "Saving..." : "Save Response"}
                  </button>
                  {currentIdx < prompts.length - 1 && (
                    <button onClick={goNext} className="neo-btn neo-btn-primary text-xs flex items-center gap-1" data-testid="reflection-next">
                      Next <ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
